import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import Image from 'next/legacy/image'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'
import useUserAgent from '@/hooks/use-user-agent'
import { makeMainLayout } from '@/layouts/main'
import { BellIcon, CloudIcon, livRentApp, MailIcon } from '@/features/Download'
import { HeaderTheme } from '@/features/Header'
import MainWrapper from '@/features/Structure/main-wrapper'
import { AndroidButton, AppleButton } from '@/components/app-buttons'
import Head from '@/components/head'

const TrendingListings = dynamic(() => import('@/features/TrendingListings'))

const DownloadWrapper = styled.div`
  background: linear-gradient(180deg, #7a9ab3 0%, #3e5b77 100%);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-top: 100px;
  min-height: 600px;
  flex-grow: 1;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);

  @media (max-width: ${breakpoints.tabletLandscape}) {
    flex-direction: column-reverse;
    align-items: center;
  }
`

const ProductContainer = styled.div`
  position: relative;
  width: 350px;
  height: 554px;
  margin-left: 25px;
  filter: drop-shadow(0px 80px 80px rgba(0, 0, 0, 0.3));

  @media (max-width: ${breakpoints.tabletLandscape}) {
    margin: 30px 0 0;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    width: 300px;
    height: 475px;
  }
`

const DownloadContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 100px;
  margin-right: 20px;
  min-height: 600px;
  max-width: 600px;

  @media (max-width: ${breakpoints.desktop}) {
    margin-left: 50px;
  }

  @media (max-width: ${breakpoints.tabletLandscape}) {
    align-items: center;
    margin: 0;
    padding: 0 20px;
    min-height: 0px;
  }
`

const Title = styled.h1`
  font-family: futura-pt-bold, Futura, sans-serif;
  font-size: 39px;
  color: ${colors.white};
  font-weight: 700;
  line-height: 45px;
  margin: 30px 0 40px;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    text-align: center;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 34px;
    line-height: 40px;
  }
`

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
`

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 40px;

  img {
    height: 25px;
    width: auto;
  }
`

const FeatureText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${colors.white};
  margin-left: 20px;

  h2 {
    font-weight: 700;
    font-size: 16px;
    line-height: 22px;
    margin: 0 0 5px;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 19px;
  }
`

const LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0;

  @media (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
  }
`

const DownloadLinkWrapper = styled.div`
  display: flex;
  justify-content: center;
  min-width: 256px;
`

const DownloadLink = styled.a`
  margin-right: 20px;

  @media (max-width: ${breakpoints.phoneMax}) {
    :last-child {
      margin-right: 0;
    }
  }
`

const CityCardsWrapper = styled.div`
  padding: 0 32px;
  margin-bottom: 100px;

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 0 18px;
    margin-bottom: 80px;
  }
`

const SubHeader = styled.h2`
  color: ${colors.regular};
  font-family: futura-pt-bold, Futura, sans-serif;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: -0.1px;
  line-height: 34px;
  margin: 80px 0 60px;
  text-align: center;

  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 28px;
    line-height: 28px;
    text-align: left;
  }
`

const DownloadLinks = () => {
  const ua = useUserAgent()
  const isAndroid = ua.match(/Android/i)
  const isIphone = ua.match(/iPhone/i)

  return (
    <DownloadLinkWrapper>
      {!isAndroid && (
        <DownloadLink href="https://click.google-analytics.com/redirect?tid=UA-113063682-1&url=https%3A%2F%2Fitunes.apple.com%2Fca%2Fapp%2Fliv%2Fid1321741040&aid=ca.livstrategies.ether&idfa=%{idfa}&cs=website&cm=index&cn=on-page">
          <AppleButton height={45} />
        </DownloadLink>
      )}
      {!isIphone && (
        <DownloadLink href="https://play.google.com/store/apps/details?id=rent.liv.ether&referrer=utm_source%3Dwebsite%26utm_medium%3Dindex%26utm_campaign%3Don-page">
          <AndroidButton height={45} />
        </DownloadLink>
      )}
    </DownloadLinkWrapper>
  )
}

const Download = () => {
  const { t } = useTranslation('download')

  const FEATURES = [
    {
      title: t('download:Reply faster, rent faster'),
      description: t(
        'download:Chat with landlords instantly. Send pictures & documents'
      ),
      icon: MailIcon
    },
    {
      title: t('download:Stay in the know'),
      description: t(
        'download:Get real-time notifications of your favourite apartment rentals'
      ),
      icon: BellIcon
    },
    {
      title: t('download:Centralized communication & storage'),
      description: t(
        'download:Store all messages & rental documents safely in one place'
      ),
      icon: CloudIcon
    }
  ]

  const metaTitle = `${t(
    'download:#1 Rental App - House & Apartment Finder'
  )} | liv.rent`

  const metaDescription = t(
    `download:Rent your perfect home with liv.rent. Search across Canada. Houses & apartments for rent in Toronto, Vancouver, Burnaby, Surrey, Coquitlam, Richmond, and more.`
  )

  return (
    <MainWrapper>
      <Head title={metaTitle} description={metaDescription} path="/download" />
      <DownloadWrapper>
        <ProductContainer>
          <Image
            src={livRentApp}
            layout="fill"
            placeholder="blur"
            sizes="350px"
            alt="Canada's best house & apartment finder rental app - liv.rent"
          />
        </ProductContainer>
        <DownloadContentWrapper>
          <Title>
            {t(
              'download:Find apartments and houses for rent on the go with the liv.rent app'
            )}
          </Title>
          <FeaturesList>
            {FEATURES.map(({ icon: Icon, ...feature }, index) => (
              <FeatureItem key={`app_feature_${index}`}>
                <IconWrapper>
                  <Icon originalSize />
                </IconWrapper>
                <FeatureText>
                  <h2>{feature.title}</h2>
                  <p>{feature.description}</p>
                </FeatureText>
              </FeatureItem>
            ))}
          </FeaturesList>
          <LinkWrapper>
            <DownloadLinks />
          </LinkWrapper>
        </DownloadContentWrapper>
      </DownloadWrapper>
      <CityCardsWrapper>
        <SubHeader>
          {t('download:Search apartments and houses for rent near you')}
        </SubHeader>
        <TrendingListings hideTitle />
      </CityCardsWrapper>
    </MainWrapper>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['translation', 'download']))
    }
  }
}

const headerConfig = {
  theme: HeaderTheme.TRANSPARENT
}

Download.Layout = makeMainLayout({ headerConfig })

export default Download
